import React, { useEffect, useState, useRef } from 'react';
import Layout from './components/Layout';
import ResponsiveShadow from './components/ResponsiveShadow';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import { Link, useNavigate } from 'react-router-dom';
import CEPMaskInput from './components/masks/CEPMaskInput';
import CelularMaskInput from './components/masks/CelularMaskInput';
import CPFMaskInput from './components/masks/CPFMaskInput';
import { formatCEP, unformatCEP } from './utils/cepUtils';
import { formatCPF, isValidCPF, unformatCPF } from './utils/cpfUtils';
import { formatCelular, unformatCelular } from './utils/celularUtils';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import PessoaPicker from './components/modals/PessoaPicker';
import PageHeader from './components/PageHeader';
import Toolbar from './components/Toolbar';
import PageContent from './components/PageContent';

const formInicial = {
  cnpjEscola: '',
  cpf: '',
  nome: '',
  email: '',
  dataNasc: '',
  sexo: '',
  estadoCivil: '',
  nacionalidade: '',
  celular: '',
  rgNum: '',
  rgUf: '',
  rgEmissor: '',
  rgDataEmissao: '',
  logrNome: '',
  logrNum: '',
  logrCompl: '',
  logrBairro: '',
  logrUf: '',
  logrCidade: '',
  logrCep: '',
};

const Diretor = () => {
  const [formData, setFormData] = useState(formInicial);
  const [ufs, setUfs] = useState([]);
  const [ufRg, setUfRg] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [emissor, setEmissor] = useState([]);
  const [pessoas, setPessoas] = useState([]);
  const [showPessoaPicker, setShowPessoaPicker] = useState(false);
  const [temDiretor, setTemDiretor] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const isAdmin = JSON.parse(localStorage.getItem('vinculo') || '{}')?.tipo === 'ADMIN';
  const isMaster = JSON.parse(localStorage.getItem('user') || '{}')?.role === 'MASTER';
  const podeImportar = isAdmin || isMaster;
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const refs = useRef({});

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    axios
      .get(`${API_BASE_URL.EMISSOR}/all`, { headers })
      .then(result => setEmissor(result.data))
      .catch(error => console.error('Erro ao obter listas Emissor:', error));
    axios
      .get(`${API_BASE_URL.UF}/all`, { headers })
      .then(result => {
        setUfRg(result.data);
        setUfs(result.data);
      })
      .catch(error => console.error('Erro ao obter listas UF:', error));
    if (podeImportar) {
      axios
        .get(`${API_BASE_URL.PESSOA}/all`, {
          params: { cnpj },
          headers,
        })
        .then(result => setPessoas(result.data))
        .catch(error => console.error('Erro ao obter lista de Pessoas:', error));
    }
  }, [token, cnpj, podeImportar]);

  // Carrega o diretor único da escola. Como só existe um, havendo vacância
  // o registro substituído é excluído antes de salvar o novo.
  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_BASE_URL.DIRETOR}/daEscola`, {
        params: { cnpj },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        if (result.status === 200 && result.data) {
          setFormData({
            ...formInicial,
            ...result.data,
            cnpjEscola: cnpj,
            cpfOriginal: result.data.cpf,
          });
          setTemDiretor(true);
        } else {
          setFormData(prev => ({ ...prev, cnpjEscola: cnpj }));
          setTemDiretor(false);
        }
      })
      .catch(error => {
        console.error('Erro ao carregar diretor da escola:', error);
        setTemDiretor(false);
      })
      .finally(() => setLoading(false));
  }, [token, cnpj]);

  // Importa os dados de uma pessoa já cadastrada no módulo Pessoas
  // (o CPF é a chave; cnpjEscola permanece o da escola da sessão).
  const handleImportarPessoa = p => {
    setFormData(prev => ({
      ...prev,
      cpf: p.cpf || '',
      nome: p.nome || '',
      email: p.email || '',
      sexo: p.sexo || '',
      estadoCivil: p.estadoCivil || '',
      nacionalidade: p.nacionalidade || '',
      celular: p.celular || '',
      rgNum: p.rgNum || '',
      rgUf: p.rgUf || '',
      rgEmissor: p.rgEmissor || '',
      rgDataEmissao: p.rgDataEmissao || '',
      logrNome: p.logrNome || '',
      logrNum: p.logrNum || '',
      logrCompl: p.logrCompl || '',
      logrBairro: p.logrBairro || '',
      logrUf: p.logrUf || '',
      logrCidade: p.logrCidade || '',
      logrCep: p.logrCep || '',
    }));
    setShowPessoaPicker(false);
  };

  useEffect(() => {
    if (formData.logrUf) {
      axios
        .get(`${API_BASE_URL.MUNICIPIO}/uf/${formData.logrUf}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(result => setCidades(result.data))
        .catch(error => console.error('Erro ao carregar cidades:', error));
    } else {
      setCidades([]);
    }
  }, [formData.logrUf, token]);

  const handleChange = e => {
    const { name, value } = e.target;

    const excludeFromUppercase = [
      'cpf',
      'logrCep',
      'rgNum',
      'logrNum',
      'rgDataEmissao',
      'dataNasc',
      'celular',
      'email',
    ];

    const processedValue = excludeFromUppercase.includes(name)
      ? value
      : typeof value === 'string'
      ? value.toUpperCase()
      : value;

    if (name === 'sexo') {
      setFormData(prev => ({
        ...prev,
        [name]: processedValue,
        estadoCivil: '',
        nacionalidade: '',
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: processedValue }));
    }
  };

  const getEstadoCivilOptions = () => {
    const isFeminino = formData.sexo === 'FEMININO';
    return [
      { value: isFeminino ? 'CASADA' : 'CASADO', label: isFeminino ? 'CASADA' : 'CASADO' },
      { value: isFeminino ? 'DIVORCIADA' : 'DIVORCIADO', label: isFeminino ? 'DIVORCIADA' : 'DIVORCIADO' },
      { value: isFeminino ? 'SOLTEIRA' : 'SOLTEIRO', label: isFeminino ? 'SOLTEIRA' : 'SOLTEIRO' },
      { value: isFeminino ? 'VIÚVA' : 'VIÚVO', label: isFeminino ? 'VIÚVA' : 'VIÚVO' },
    ];
  };

  const getNacionalidadeOptions = () => {
    const isFeminino = formData.sexo === 'FEMININO';
    return [
      { value: isFeminino ? 'BRASILEIRA' : 'BRASILEIRO', label: isFeminino ? 'BRASILEIRA' : 'BRASILEIRO' },
      { value: isFeminino ? 'NATURALIZADA' : 'NATURALIZADO', label: isFeminino ? 'NATURALIZADA' : 'NATURALIZADO' },
      { value: isFeminino ? 'ESTRANGEIRA' : 'ESTRANGEIRO', label: isFeminino ? 'ESTRANGEIRA' : 'ESTRANGEIRO' },
    ];
  };

  const handleSubmit = e => {
    e.preventDefault();

    const camposObrigatorios = [
      { campo: 'cpf', label: 'CPF' },
      { campo: 'nome', label: 'Nome' },
      { campo: 'sexo', label: 'Sexo' },
      { campo: 'estadoCivil', label: 'Estado Civil' },
      { campo: 'nacionalidade', label: 'Nacionalidade' },
      { campo: 'logrNome', label: 'Logradouro Nome' },
      { campo: 'logrNum', label: 'Número' },
      { campo: 'logrBairro', label: 'Bairro' },
      { campo: 'logrUf', label: 'UF' },
      { campo: 'logrCidade', label: 'Cidade' },
      { campo: 'logrCep', label: 'CEP' },
    ];
    const faltando = camposObrigatorios.find(c => {
      const v = formData[c.campo];
      return v === undefined || v === null || String(v).trim() === '';
    });
    if (faltando) {
      showDefaultModal('error', `O campo "${faltando.label}" é obrigatório.`);
      return;
    }

    if (!isValidCPF(formData.cpf)) {
      showDefaultModal('error', 'CPF inválido');
      return;
    }

    const dataToSave = {
      ...formData,
      cnpjEscola: formData.cnpjEscola || cnpj,
      cpf: unformatCPF(formData.cpf),
      logrCep: unformatCEP(formData.logrCep),
      celular: unformatCelular(formData.celular),
    };

    // Havendo vacância/troca de diretor, o registro anterior é excluído
    // para manter apenas um diretor por escola.
    const cpfAnterior = formData.cpfOriginal;
    if (cpfAnterior && cpfAnterior !== dataToSave.cpf) {
      axios
        .delete(`${API_BASE_URL.DIRETOR}/delete`, {
          params: { cnpjEscola: dataToSave.cnpjEscola, cpf: cpfAnterior },
          headers: { Authorization: `Bearer ${token}` },
        })
        .catch(error =>
          console.error('Erro ao excluir diretor anterior:', error)
        );
    }

    axios
      .post(`${API_BASE_URL.DIRETOR}/save`, dataToSave, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        setTemDiretor(true);
        showDefaultModal('success', 'Diretor salvo com sucesso!');
      })
      .catch(error => {
        console.error('Erro ao salvar formData:', error);
        showDefaultModal('error', 'Erro ao salvar o registro.');
      });
  };

  const handleExcluir = () => {
    if (!formData.cpf) return;
    showConfirmModal('delete', () => {
      axios
        .delete(`${API_BASE_URL.DIRETOR}/delete`, {
          params: { cnpjEscola: cnpj, cpf: unformatCPF(formData.cpf) },
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(() => {
          setFormData({ ...formInicial, cnpjEscola: cnpj });
          setTemDiretor(false);
          showDefaultModal('success', 'Diretor excluído com sucesso!');
        })
        .catch(error => {
          console.error('Erro ao excluir diretor:', error);
          showDefaultModal('error', 'Erro ao excluir o diretor.');
        });
    });
  };

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader
          title="Diretor da Escola"
          icon="bi-person-badge"
          closeRoute="/home"
        />
        <PageContent>
          <Toolbar>
            {podeImportar && (
              <button
                type="button"
                className="btn btn-primary btn-sm botao-igual"
                onClick={() => setShowPessoaPicker(true)}
              >
                <i className="bi bi-person-plus me-1" />Importar de Pessoas
              </button>
            )}
          </Toolbar>
          <form id="diretor-form" onSubmit={handleSubmit}>
            <div className="row g-3 small">
              {/* Nome */}
              <div className="col-12 col-sm-6 col-md-5 input-group-sm">
                <label htmlFor="nome" className="form-label">
                  Nome
                </label>
                <input
                  type="text"
                  className="form-control text-uppercase"
                  id="nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* CPF */}
              <div className="col-12 col-sm-6 col-md-2 input-group-sm">
                <label htmlFor="cpf" className="form-label">
                  CPF
                </label>
                <CPFMaskInput
                  id="cpf"
                  value={formatCPF(formData.cpf)}
                  onChange={e =>
                    handleChange({
                      target: {
                        name: 'cpf',
                        value: unformatCPF(e.target.value),
                      },
                    })
                  }
                  name="cpf"
                  type="text"
                  required
                />
              </div>

              {/* Data de nascimento */}
              <div className="col-12 col-sm-6 col-md-3 input-group-sm">
                <label htmlFor="dataNasc" className="form-label">
                  Data de Nascimento
                </label>
                <input
                  type="date"
                  className="form-control"
                  id="dataNasc"
                  name="dataNasc"
                  value={formData.dataNasc}
                  onChange={handleChange}
                />
              </div>

              {/* Sexo */}
              <div className="col-12 col-sm-6 col-md-2 input-group-sm">
                <label htmlFor="sexo" className="form-label">
                  Sexo
                </label>
                <select
                  className="form-control"
                  name="sexo"
                  value={formData.sexo}
                  onChange={handleChange}
                  required
                >
                  <option value="">Selecione...</option>
                  <option value="FEMININO">FEMININO</option>
                  <option value="MASCULINO">MASCULINO</option>
                </select>
              </div>

              {/* Estado Civil */}
              <div className="col-12 col-sm-6 col-md-3 input-group-sm">
                <label htmlFor="estadoCivil" className="form-label">
                  Estado Civil
                </label>
                <select
                  className="form-control"
                  name="estadoCivil"
                  value={formData.estadoCivil}
                  onChange={handleChange}
                  disabled={!formData.sexo}
                  required
                >
                  <option value="">Selecione...</option>
                  {formData.sexo &&
                    getEstadoCivilOptions().map((option, index) => (
                      <option key={index} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                </select>
              </div>

              {/* Nacionalidade */}
              <div className="col-12 col-sm-6 col-md-3 input-group-sm">
                <label htmlFor="nacionalidade" className="form-label">
                  Nacionalidade
                </label>
                <select
                  className="form-control"
                  name="nacionalidade"
                  value={formData.nacionalidade}
                  onChange={handleChange}
                  disabled={!formData.sexo}
                  required
                >
                  <option value="">Selecione...</option>
                  {formData.sexo &&
                    getNacionalidadeOptions().map((option, index) => (
                      <option key={index} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                </select>
              </div>

              {/* RG */}
              <div className="col-12 col-sm-6 col-md-2 input-group-sm">
                <label htmlFor="rgNum" className="form-label">
                  Nº RG
                </label>
                <input
                  type="text"
                  className="form-control text-uppercase"
                  id="rgNum"
                  name="rgNum"
                  value={formData.rgNum}
                  onChange={handleChange}
                />
              </div>

              <div className="col-12 col-sm-6 col-md-1 input-group-sm">
                <label htmlFor="rgEmissor" className="form-label">
                  Emissor RG
                </label>
                <select
                  id="rgEmissor"
                  className="form-control small"
                  value={formData.rgEmissor}
                  onChange={handleChange}
                  name="rgEmissor"
                >
                  <option value="">Selecione...</option>
                  {emissor.map((e, index) => (
                    <option key={index} value={e.codigo}>
                      {e.codigo}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12 col-sm-6 col-md-2 input-group-sm">
                <label htmlFor="rgDataEmissao" className="form-label">
                  Data Emissão RG
                </label>
                <input
                  type="date"
                  className="form-control"
                  id="rgDataEmissao"
                  name="rgDataEmissao"
                  value={formData.rgDataEmissao}
                  onChange={handleChange}
                />
              </div>

              <div className="col-12 col-sm-6 col-md-1 input-group-sm">
                <label htmlFor="rgUf" className="form-label">
                  UF RG
                </label>
                <select
                  id="rgUf"
                  className="form-control small"
                  value={formData.rgUf}
                  onChange={handleChange}
                  name="rgUf"
                >
                  <option value="">Selecione...</option>
                  {ufRg.map((e, index) => (
                    <option key={index} value={e.sigla}>
                      {e.sigla}
                    </option>
                  ))}
                </select>
              </div>

              {/* Celular */}
              <div className="col-12 col-sm-6 col-md-3 input-group-sm">
                <label htmlFor="celular" className="form-label">
                  Celular
                </label>
                <CelularMaskInput
                  value={formatCelular(formData.celular)}
                  onChange={e =>
                    handleChange({
                      target: {
                        name: 'celular',
                        value: unformatCelular(e.target.value),
                      },
                    })
                  }
                  name="celular"
                  id="celular"
                />
              </div>

              {/* Email */}
              <div className="col-12 col-sm-6 col-md-6 input-group-sm">
                <label htmlFor="email" className="form-label">
                  Email
                </label>
                <input
                  type="email"
                  className="form-control"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="email@exemplo.com"
                />
              </div>

              <div className="col-12"></div>

              {/* Endereço */}
              <div className="col-12 col-sm-6 col-md-4 input-group-sm">
                <label htmlFor="logrNome" className="form-label">
                  Logradouro Nome
                </label>
                <input
                  type="text"
                  className="form-control text-uppercase"
                  id="logrNome"
                  name="logrNome"
                  value={formData.logrNome}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-12 col-sm-6 col-md-1 input-group-sm">
                <label htmlFor="logrNum" className="form-label">
                  Número
                </label>
                <input
                  type="text"
                  className="form-control text-uppercase"
                  id="logrNum"
                  name="logrNum"
                  value={formData.logrNum}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-12 col-sm-6 col-md-3 input-group-sm">
                <label htmlFor="logrCompl" className="form-label">
                  Complemento
                </label>
                <input
                  type="text"
                  className="form-control text-uppercase"
                  id="logrCompl"
                  name="logrCompl"
                  value={formData.logrCompl}
                  onChange={handleChange}
                />
              </div>

              <div className="col-12 col-sm-6 col-md-2 input-group-sm">
                <label htmlFor="logrBairro" className="form-label">
                  Bairro
                </label>
                <input
                  type="text"
                  className="form-control text-uppercase"
                  id="logrBairro"
                  name="logrBairro"
                  value={formData.logrBairro}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-12 col-sm-6 col-md-2 input-group-sm">
                <label className="form-label">CEP</label>
                <CEPMaskInput
                  value={formatCEP(formData.logrCep)}
                  onChange={e =>
                    handleChange({
                      target: {
                        name: 'logrCep',
                        value: unformatCEP(e.target.value),
                      },
                    })
                  }
                  name="logrCep"
                  ref={el => (refs.current.cep = el)}
                  type="text"
                  required
                />
              </div>

              <div className="col-12 col-sm-6 col-md-1 input-group-sm">
                <label className="form-label">UF</label>
                <select
                  className="form-control small"
                  value={formData.logrUf}
                  onChange={handleChange}
                  name="logrUf"
                  required
                >
                  <option value="">UF</option>
                  {ufs.map((uf, index) => (
                    <option key={index} value={uf.sigla}>
                      {uf.sigla}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12 col-sm-6 col-md-4 input-group-sm">
                <label className="form-label">Cidade</label>
                <select
                  className="form-control"
                  value={formData.logrCidade}
                  onChange={handleChange}
                  name="logrCidade"
                  disabled={!formData.logrUf}
                  required
                >
                  <option value="">Selecione uma cidade...</option>
                  {cidades.map((cidade, index) => (
                    <option key={index} value={cidade.nome}>
                      {cidade.nome}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </form>
        </PageContent>

        <div
          style={{
            padding: '1rem',
            borderTop: '1px solid rgba(0,0,0,0.1)',
            display: 'flex',
            gap: '0.5rem',
            backgroundColor: '#f8f9fa',
          }}
        >
          <button type="submit" form="diretor-form" className="btn btn-success btn-sm">
            {temDiretor ? 'Substituir Diretor' : 'Salvar'}
          </button>
          {temDiretor && (
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={handleExcluir}
            >
              <i className="bi bi-trash me-1" />Excluir
            </button>
          )}
          <Link className="btn btn-secondary btn-sm" to="/home">
            Cancelar
          </Link>
        </div>
      </ResponsiveShadow>

      <DefaultModal {...setDefaultModal} />
      <ConfirmModal {...setConfirmModal} />

      {podeImportar && (
        <PessoaPicker
          show={showPessoaPicker}
          pessoas={pessoas}
          onSelect={handleImportarPessoa}
          onClose={() => setShowPessoaPicker(false)}
        />
      )}
    </Layout>
  );
};

export default Diretor;
